import { Request, Response, NextFunction } from 'express';
import { AuthRequest } from '../../middleware/auth.middleware';
import * as service from './users.service';
import { createUserSchema, updateUserSchema, searchUserSchema, assignVisibilitySchema } from './users.schema';
import { getIo } from '../../realtime/socket.bootstrap';
import { SocketEvents } from '@ichat/shared-types';

export async function list(req: Request, res: Response, next: NextFunction) {
  try {
    const authReq = req as AuthRequest;
    let limit = req.query.limit ? parseInt(req.query.limit as string) : undefined;
    if (limit !== undefined && isNaN(limit)) limit = undefined;
    
    let cursor = req.query.cursor as string | undefined;
    if (cursor === 'undefined' || cursor === 'null' || cursor === '') cursor = undefined;

    const users = await service.listUsers(undefined, authReq.user?.sub, limit, cursor);
    res.json({ success: true, data: users });
  } catch (err) { next(err); }
}

export async function getById(req: Request, res: Response, next: NextFunction) {
  try {
    const user = await service.getUserById(req.params.id);
    res.json({ success: true, data: user });
  } catch (err) { next(err); }
}

export async function getByUsername(req: Request, res: Response, next: NextFunction) {
  try {
    const user = await service.getUserByUsername(req.params.username);
    res.json({ success: true, data: user });
  } catch (err) { next(err); }
}

export async function create(req: Request, res: Response, next: NextFunction) {
  try {
    const data = createUserSchema.parse(req.body);
    const user = await service.createUser(data);
    getIo()?.emit(SocketEvents.USER_CREATED, user);
    res.status(201).json({ success: true, data: user });
  } catch (err) { next(err); }
}

export async function update(req: Request, res: Response, next: NextFunction) {
  try {
    const data = updateUserSchema.parse(req.body);
    const user = await service.updateUser(req.params.id, data);
    getIo()?.emit(SocketEvents.USER_UPDATED, user);
    res.json({ success: true, data: user });
  } catch (err) { next(err); }
}

export async function updateMe(req: AuthRequest, res: Response, next: NextFunction) {
  try {
    const data = updateUserSchema.parse(req.body);
    const user = await service.updateProfile(req.user!.sub, data);
    getIo()?.emit(SocketEvents.USER_UPDATED, user);
    res.json({ success: true, data: user });
  } catch (err) { next(err); }
}

export async function activate(req: Request, res: Response, next: NextFunction) {
  try {
    const user = await service.activateUser(req.params.id);
    getIo()?.emit(SocketEvents.USER_ACTIVATED, { id: req.params.id, user });
    res.json({ success: true, data: user });
  } catch (err) { next(err); }
}

export async function deactivate(req: Request, res: Response, next: NextFunction) {
  try {
    const user = await service.deactivateUser(req.params.id);
    getIo()?.emit(SocketEvents.USER_DEACTIVATED, { id: req.params.id, user });
    res.json({ success: true, data: user });
  } catch (err) { next(err); }
}

export async function destroy(req: Request, res: Response, next: NextFunction) {
  try {
    await service.deleteUser(req.params.id);
    getIo()?.emit(SocketEvents.USER_DELETED, { id: req.params.id });
    res.json({ success: true, message: 'User deleted' });
  } catch (err) { next(err); }
}

export async function search(req: Request, res: Response, next: NextFunction) {
  try {
    const authReq = req as AuthRequest;
    const { query, limit } = searchUserSchema.parse(req.query);
    const users = await service.searchUsers(query, limit, authReq.user?.sub);
    res.json({ success: true, data: users });
  } catch (err) { next(err); }
}

export async function presenceBulk(req: Request, res: Response, next: NextFunction) {
  try {
    const { userIds } = req.body as { userIds: string[] };
    const presence = await service.getUserPresenceBulk(userIds ?? []);
    res.json({ success: true, data: presence });
  } catch (err) { next(err); }
}

export async function assignVisibility(req: Request, res: Response, next: NextFunction) {
  try {
    const { id } = req.params;
    const { visibleUserIds } = assignVisibilitySchema.parse(req.body);
    const user = await service.assignVisibility(id, visibleUserIds);
    getIo()?.emit(SocketEvents.USER_UPDATED, user);
    res.json({ success: true, data: user });
  } catch (err) { next(err); }
}
